import type React from 'react';
import classes from './profile-page.module.scss';
import { useProfilePageController } from './useProfilePageController';

export const ProfilePage: React.FC = () => {
  const {
    user,
    isManager,
    isLoading,
    isSaving,
    error,
    profile,
    form,
    setForm,
    sectionOptions,
    loadProfile,
    onSubmit,
  } = useProfilePageController();

  if (isLoading) {
    return (
      <section className="contentCard" dir="rtl">
        <h1 className="homeTitle">פרטים אישיים</h1>
        <p className="homeHint">טוען פרופיל...</p>
      </section>
    );
  }

  if (error && !form) {
    return (
      <section className="contentCard" dir="rtl">
        <h1 className="homeTitle">פרטים אישיים</h1>
        <p className="errorText inlineMessage">{error}</p>
        <button
          className="button retryButton"
          type="button"
          onClick={() => {
            void loadProfile();
          }}
        >
          נסה שוב
        </button>
      </section>
    );
  }

  return (
    <div className="profilePage" dir="rtl">
      <header className="profilePageHeader">
        <div>
          <h1>הפרופיל שלי</h1>
          <p>ניהול הפרטים האישיים שלך</p>
        </div>
        <span className={classes.profileTenantName}>{user?.tenantCompanyName}</span>
      </header>

      {error && <p className={`errorText ${classes.profileMessage}`}>{error}</p>}

      {form && profile && (
        <form className={classes.profileForm} onSubmit={onSubmit}>
          <section className="profilePanel" aria-labelledby="profile-auth-title">
            <div className="profilePanelHeader">
              <h2 id="profile-auth-title">אימות</h2>
            </div>
            <div className={classes.profileAuthRow}>
              <div className={`profileField ${classes.profileUsernameField}`}>
                <span>שם משתמש</span>
                <div className={classes.profileReadonlyValue} dir="ltr">
                  {user?.username}
                </div>
              </div>
            </div>
          </section>

          <section className="profilePanel" aria-labelledby="profile-details-title">
            <div className="profilePanelHeader">
              <h2 id="profile-details-title">פרטים אישיים</h2>
            </div>
            <div className={classes.profileIdentityLayout}>
              <div className={classes.profilePhotoColumn}>
                <div className={classes.profilePhotoFrame}>
                  {profile.photoUrl ? (
                    <img src={profile.photoUrl} alt="התמונה האישית שלי" />
                  ) : (
                    <span aria-hidden="true">
                      {`${form.firstName.charAt(0)}${form.lastName.charAt(0)}` || 'א'}
                    </span>
                  )}
                </div>
              </div>

              <div className={classes.profileFieldsGrid}>
                <div className="profileField">
                  <label htmlFor="profile-first-name">שם פרטי</label>
                  <input
                    id="profile-first-name"
                    value={form.firstName}
                    onChange={(event) => setForm({ ...form, firstName: event.target.value })}
                  />
                </div>
                <div className="profileField">
                  <label htmlFor="profile-last-name">שם משפחה</label>
                  <input
                    id="profile-last-name"
                    value={form.lastName}
                    onChange={(event) => setForm({ ...form, lastName: event.target.value })}
                  />
                </div>
                <div className="profileField">
                  <label htmlFor="profile-email">כתובת אימייל</label>
                  <input
                    id="profile-email"
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                    dir="ltr"
                  />
                </div>
                <div className="profileField">
                  <label htmlFor="profile-phone">טלפון נייד</label>
                  <input
                    id="profile-phone"
                    value={form.phoneNumber}
                    onChange={(event) => setForm({ ...form, phoneNumber: event.target.value })}
                    dir="ltr"
                  />
                </div>
                <fieldset className={`profileField ${classes.profileSexField}`}>
                  <legend>מין</legend>
                  <div className={classes.profileSegmentedControl}>
                    <label className={form.sex === 'male' ? 'active' : ''}>
                      <input
                        type="radio"
                        name="profile-sex"
                        checked={form.sex === 'male'}
                        onChange={() => setForm({ ...form, sex: 'male' })}
                      />
                      זכר
                    </label>
                    <label className={form.sex === 'female' ? 'active' : ''}>
                      <input
                        type="radio"
                        name="profile-sex"
                        checked={form.sex === 'female'}
                        onChange={() => setForm({ ...form, sex: 'female' })}
                      />
                      נקבה
                    </label>
                  </div>
                </fieldset>
                <div className="profileField">
                  <label htmlFor="profile-birthdate">תאריך לידה</label>
                  <input
                    id="profile-birthdate"
                    type="date"
                    value={form.birthdate}
                    onChange={(event) => setForm({ ...form, birthdate: event.target.value })}
                  />
                </div>
                <div className="profileField">
                  <label htmlFor="profile-id-number">תעודת זהות</label>
                  <input
                    id="profile-id-number"
                    value={form.idNumber}
                    onChange={(event) => setForm({ ...form, idNumber: event.target.value })}
                    dir="ltr"
                  />
                </div>
                <div className="profileField">
                  <label htmlFor="profile-section">מחלקה</label>
                  {isManager ? (
                    <select
                      id="profile-section"
                      value={form.sectionPk}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          sectionPk: event.target.value === '' ? '' : Number(event.target.value),
                        })
                      }
                    >
                      <option value="">ללא שיוך</option>
                      {sectionOptions.map((section) => (
                        <option key={section.id} value={section.id}>
                          {section.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className={classes.profileReadonlyValue}>{profile.sectionName ?? 'ללא שיוך'}</div>
                  )}
                </div>
              </div>
            </div>
          </section>

          <div className={classes.profileSaveBar}>
            <button className="profileSaveButton" type="submit" disabled={isSaving}>
              {isSaving ? 'שומר...' : 'שמירת שינויים'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
